<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="store.getters.dialogsCloseOnEscape"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
  >
    <template #header>
      {{ title }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div
      :class="[loadingForm ? 'flex' : 'hidden']"
      class="justify-content-center align-items-center h-full"
    >
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>

    <Form
      ref="formRef"
      v-bind="formConfigRef"
      :class="[loadingForm ? 'hidden' : 'flex']"
    />

    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button label="Cancelar" severity="secondary" @click="closeModal()" />
          <Button
            :label="title"
            @click="save"
            :loading="loading || loadingForm"
            loading-icon="pi pi-spinner"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { useStore } from "vuex";
import { useConfirm } from "primevue/useconfirm";
import { Form, useImmer } from "@sttedra_ui/framework";

import useFormConfig from "@/pages/_root/workspaces-dialog/workspaces-dialog-form.view-config.js";
import {
  workspacesCreate,
  workspacesUpdate,
} from "@/api/workspaces/workspaces.api.js";
import {
  validateGUID,
  removeCustomErrors,
  addCustomErrors,
  checkFormHasData,
  handleShowConfirm,
} from "@/composables/_commons/helper.js";

// #region Props, Emits
const emit = defineEmits(["saved"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const loadingForm = ref(false);
const loading = ref(false);
const store = useStore();
const confirm = useConfirm();
const toast = useToast();
const showModalRef = ref(false);
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef } = useImmer(formConfig);
const register = ref(null);

const title = computed(() => {
  return (register.value?.guidWorkspace ? "Editar" : "Crear") + " workspace";
});
// #endregion

// #region Functions
async function showModal(data) {
  loadingForm.value = true;
  formConfig.value = new useFormConfig(null);

  showModalRef.value = true;
  register.value = null;

  if (data != null) {
    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object);
  }

  loadingForm.value = false;
}

function handleResponse(response) {
  if (response.status == 200) {
    toast.add({
      severity: "success",
      summary: `Registro ${
        register.value?.guidWorkspace ? "editado" : "creado"
      }`,
      detail: `${
        register.value?.guidWorkspace ? "Edición" : "Creación"
      } realizada correctamente`,
      life: 3000,
    });

    showModalRef.value = false;

    emit("saved", response.data);
  } else {
    const error = response.data?.errors?.[0]?.message;

    let errorMessage = `No se ha podido realizar la ${
      register.value?.guidWorkspace ? "edición" : "creación"
    }`;

    if (error) {
      errorMessage = error;
    }

    toast.add({
      severity: "error",
      summary: "Error",
      detail: errorMessage,
      life: 3000,
    });
  }
}

async function save() {
  let invalid = !(await formRef.value?.v$Ref.$validate());

  const data = { ...formRef.value.state$$.values };

  // Get the guidWorkspace div
  const guidDiv = document.querySelector(".guid-custom-input");

  // Remove .custom-error inside guidWorkspace if exists
  removeCustomErrors(".guid-custom-input");

  const guidValue = data.guidWorkspace;

  if (guidValue) {
    const validGUID = validateGUID(guidValue);

    if (!validGUID) {
      invalid = true;
      addCustomErrors(guidDiv, "El GUID no es válido");
    }
  }

  if (invalid) return;

  loading.value = true;

  let response = null;

  if (register.value?.guidWorkspace) {
    data.id = register.value.guidWorkspace;
    response = await workspacesUpdate(register.value.guidWorkspace, data);
  } else {
    response = await workspacesCreate(data);
  }

  handleResponse(response);

  loading.value = false;
}

function closeModal() {
  let showConfirm = false;

  if (checkFormHasData({ ...formRef.value.state$$.values })) {
    showConfirm = true;
  }

  if (showConfirm) {
    handleShowConfirm(confirm, showModalRef);
  } else {
    showModalRef.value = false;
  }
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>
